<template>
  <div class="module-tree-scroll-region d-flex flex-column flex-grow-1" style="min-height: 0">
    <!-- 핀은 overlay — 가상 스크롤 높이를 빼앗지 않음 (스크롤 중 높이 변동 방지) -->
    <div class="module-tree-sticky-overlay">
    <div
      v-for="sticky in pinnedStickyRows"
      :key="`pinned_${sticky.kind}_${sticky.row.item.id}`"
      class="module-tree-sticky-header"
      :style="{ height: rowHeight + 'px' }"
    >
      <TreeNodeRow
        :item="sticky.row.item"
        :level="sticky.row.level"
        :is-last-child="sticky.row.isLastChild"
        :has-children="sticky.row.hasChildren"
        :expanded="sticky.row.expanded"
        :pinned="true"
        :is-selected="resolveSelected(sticky.row.item)"
        :selected-site="selectedSite"
        :focused-node-id="focusedNodeId"
        @toggle-expand="handleToggleExpand"
        @node-selected="$emit('node-selected', $event)"
        @node-focused="$emit('node-focused', $event)"
        @node-blurred="$emit('node-blurred', $event)"
        @keyboard-navigate="$emit('keyboard-navigate', $event)"
        @node-moved="$emit('node-moved', $event)"
        @name-changed="$emit('name-changed', $event)"
        @rename-requested="$emit('rename-requested', $event)"
        @move-requested="$emit('move-requested', $event)"
        @delete-requested="$emit('delete-requested', $event)"
        @show-message="$emit('show-message', $event)"
      />
    </div>
    </div>

    <v-virtual-scroll
      ref="virtualScroll"
      :items="flatRows"
      :item-height="rowHeight"
      :bench="12"
      class="module-tree-virtual-scroll flex-grow-1"
      style="min-height: 0"
      @scroll.native="onTreeVirtualScroll"
    >
      <template #default="{ item: row }">
        <div
          class="module-tree-virtual-row"
          :style="{ height: rowHeight + 'px' }"
        >
          <TreeNodeRow
            v-show="!shouldHideRowForPin(row)"
          :key="row.item.id"
          :item="row.item"
          :level="row.level"
          :is-last-child="row.isLastChild"
          :has-children="row.hasChildren"
          :expanded="row.expanded"
          :is-selected="resolveSelected(row.item)"
          :selected-site="selectedSite"
          :focused-node-id="focusedNodeId"
          @toggle-expand="handleToggleExpand"
          @node-selected="$emit('node-selected', $event)"
          @node-focused="$emit('node-focused', $event)"
          @node-blurred="$emit('node-blurred', $event)"
          @keyboard-navigate="$emit('keyboard-navigate', $event)"
          @node-moved="$emit('node-moved', $event)"
          @name-changed="$emit('name-changed', $event)"
          @rename-requested="$emit('rename-requested', $event)"
          @move-requested="$emit('move-requested', $event)"
          @delete-requested="$emit('delete-requested', $event)"
          @show-message="$emit('show-message', $event)"
        />
        </div>
      </template>
    </v-virtual-scroll>
  </div>
</template>

<script>
import TreeNodeRow from './TreeNodeRow.vue';
import {
  flattenModuleTreeRows,
  createDefaultExpandedState,
  defaultExpandedForTreeNode,
  computeModuleTreeSignature,
  isIncrementalTreeSignatureChange
} from '@/features/panel-planning/utils/flattenModuleTreeRows';
import { getPanelTreeModuleChildren } from '@/features/panel-planning/utils/panelTreeModuleChildrenIndex';
import { resolvePinnedModuleTreeHierarchyRows } from '@/features/panel-planning/utils/resolvePinnedModuleTreeHierarchyRows';
import { shouldHidePinnedVirtualRow } from '@/ui/utils/resolvePinnedVirtualScrollHeaders';

export const MODULE_TREE_ROW_HEIGHT = 44;

export default {
  name: 'PanelPlanningVirtualTree',
  components: {
    TreeNodeRow
  },
  props: {
    treeData: {
      type: Array,
      default: () => []
    },
    focusedNodeId: {
      type: [String, Number],
      default: null
    },
    selectionUiVersion: {
      type: Number,
      default: 0
    },
    selectedSite: {
      type: Object,
      default: null
    },
    isSelectedFn: {
      type: Function,
      required: true
    },
    /** 트리 패널 v-show — false일 때 높이 0으로 VVirtualScroll 이 잘못 계산됨 */
    panelVisible: {
      type: Boolean,
      default: true
    },
    /** in-place 트리 변경 시 flatRows 재계산 트리거 (treeData 참조는 동일) */
    treeRevision: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      rowHeight: MODULE_TREE_ROW_HEIGHT,
      expandedIds: {},
      lastTreeSignature: '',
      resizeObserver: null,
      /** 스크롤 시 상단에 고정할 펼쳐진 Array / String 행 */
      pinnedArrayRow: null,
      pinnedStringRow: null,
      _treeVirtualScrollEl: null,
      _treeVirtualScrollEls: []
    };
  },
  computed: {
    flatRows() {
      void this.treeRevision;
      return flattenModuleTreeRows(this.treeData, this.expandedIds);
    },
    pinnedStickyRows() {
      const rows = [];
      if (this.pinnedArrayRow) {
        rows.push({ kind: 'array', row: this.pinnedArrayRow });
      }
      if (this.pinnedStringRow) {
        rows.push({ kind: 'string', row: this.pinnedStringRow });
      }
      return rows;
    }
  },
  watch: {
    'selectedSite.id': {
      handler(siteId, prevId) {
        if (siteId != null && siteId !== prevId) {
          this.applyDefaultExpandedState(this.treeData);
          this.scheduleVirtualScrollRefresh();
        }
      }
    },
    treeData: {
      handler(nodes) {
        const sig = this.computeTreeSignature(nodes);
        const preserveScroll = Boolean(this.lastTreeSignature) && sig === this.lastTreeSignature;
        if (sig !== this.lastTreeSignature) {
          const prev = this.lastTreeSignature;
          const isIncremental = isIncrementalTreeSignatureChange(prev, sig);
          this.lastTreeSignature = sig;
          if (isIncremental) {
            this.syncExpandedState(nodes);
          } else {
            this.applyDefaultExpandedState(nodes);
          }
        } else {
          this.syncExpandedState(nodes);
        }
        this.scheduleVirtualScrollRefresh({ preserveScroll });
      },
      immediate: true
    },
    flatRows() {
      this.scheduleVirtualScrollRefresh({ preserveScroll: true });
      this.$nextTick(this.updatePinnedHierarchyRows);
    },
    treeRevision() {
      this.scheduleVirtualScrollRefresh({ preserveScroll: true });
    },
    panelVisible(visible) {
      if (visible) {
        this.scheduleVirtualScrollRefresh();
        this.$nextTick(this.attachTreeVirtualScrollListener);
      } else {
        this.pinnedArrayRow = null;
        this.pinnedStringRow = null;
      }
    }
  },
  mounted() {
    this.attachResizeObserver();
    this.scheduleVirtualScrollRefresh();
    this.$nextTick(this.attachTreeVirtualScrollListener);
  },
  beforeDestroy() {
    this.detachResizeObserver();
    this.detachTreeVirtualScrollListener();
  },
  methods: {
    computeTreeSignature(nodes) {
      return computeModuleTreeSignature(nodes);
    },

    /** 트리 구조 변경·사이트 전환 시 기본 펼침 — 전부 접힘 */
    applyDefaultExpandedState(nodes) {
      const next = createDefaultExpandedState(nodes, null);
      Object.keys(this.expandedIds).forEach(id => {
        this.$delete(this.expandedIds, id);
      });
      Object.entries(next).forEach(([id, expanded]) => {
        this.$set(this.expandedIds, id, expanded);
      });
    },

    syncExpandedState(nodes) {
      const ids = new Set();
      const walk = (nodeList) => {
        if (!nodeList || !Array.isArray(nodeList)) {
          return;
        }
        nodeList.forEach((item) => {
          if (item.children?.length > 0 && item.id != null) {
            ids.add(item.id);
            if (!Object.prototype.hasOwnProperty.call(this.expandedIds, item.id)) {
              this.$set(
                this.expandedIds,
                item.id,
                defaultExpandedForTreeNode(item)
              );
            }
            walk(item.children);
          }
        });
      };
      walk(nodes || []);
      Object.keys(this.expandedIds).forEach((id) => {
        if (!ids.has(id)) {
          this.$delete(this.expandedIds, id);
        }
      });
    },

    scheduleVirtualScrollRefresh({ preserveScroll = false } = {}) {
      const scrollTop = preserveScroll ? this.getTreeVirtualScrollTop() : 0;
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.refreshVirtualScrollLayout();
          if (preserveScroll && scrollTop > 0) {
            const el = this._treeVirtualScrollEl ?? this.$refs.virtualScroll?.$el;
            if (el) {
              el.scrollTop = scrollTop;
            }
          }
          // v-show 전환 후 컨테이너 높이가 0이면 강제 재계산
          const el = this.$el;
          if (el && el.clientHeight === 0 && this.panelVisible) {
            setTimeout(() => {
              this.refreshVirtualScrollLayout();
              if (preserveScroll && scrollTop > 0) {
                const scrollEl = this._treeVirtualScrollEl ?? this.$refs.virtualScroll?.$el;
                if (scrollEl) {
                  scrollEl.scrollTop = scrollTop;
                }
              }
              window.dispatchEvent(new Event('resize'));
            }, 100);
          }
        });
      });
    },

    refreshVirtualScrollLayout() {
      const vs = this.$refs.virtualScroll;
      if (!vs) return;
      if (typeof vs.onScroll === 'function') {
        vs.onScroll();
      } else if (typeof vs.update === 'function') {
        vs.update();
      }
      this.attachTreeVirtualScrollListener();
      this.updatePinnedHierarchyRows();
    },

    attachResizeObserver() {
      if (typeof ResizeObserver === 'undefined') {
        return;
      }
      this.detachResizeObserver();
      const el = this.$el;
      if (!el) {
        return;
      }
      this.resizeObserver = new ResizeObserver(() => {
        if (this.panelVisible) {
          this.refreshVirtualScrollLayout();
        }
      });
      this.resizeObserver.observe(el);
    },

    detachResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },

    attachTreeVirtualScrollListener() {
      this.detachTreeVirtualScrollListener();
      const root = this.$refs.virtualScroll?.$el;
      if (!root) return;
      this._treeVirtualScrollEl = root;
      this._treeVirtualScrollEls = [root];
      root.addEventListener('scroll', this.onTreeVirtualScroll, { passive: true });
      this.updatePinnedHierarchyRows();
    },

    detachTreeVirtualScrollListener() {
      if (this._treeVirtualScrollEls?.length) {
        this._treeVirtualScrollEls.forEach((el) => {
          el.removeEventListener('scroll', this.onTreeVirtualScroll);
        });
      }
      this._treeVirtualScrollEls = [];
      this._treeVirtualScrollEl = null;
    },

    getTreeVirtualScrollTop() {
      return this._treeVirtualScrollEl?.scrollTop
        ?? this.$refs.virtualScroll?.$el?.scrollTop
        ?? 0;
    },

    shouldHideRowForPin(row) {
      return shouldHidePinnedVirtualRow(
        row,
        this.pinnedStickyRows,
        (a, b) => String(a?.item?.id) === String(b?.item?.id)
      );
    },

    onTreeVirtualScroll() {
      this.updatePinnedHierarchyRows();
    },

    updatePinnedHierarchyRows() {
      const { arrayRow, stringRow } = resolvePinnedModuleTreeHierarchyRows({
        flatRows: this.flatRows,
        scrollTop: this.getTreeVirtualScrollTop(),
        rowHeight: this.rowHeight
      });
      const prevArrayKey = this.pinnedArrayRow?.item?.id ?? null;
      const nextArrayKey = arrayRow?.item?.id ?? null;
      const prevStringKey = this.pinnedStringRow?.item?.id ?? null;
      const nextStringKey = stringRow?.item?.id ?? null;
      if (prevArrayKey === nextArrayKey && prevStringKey === nextStringKey) {
        return;
      }
      this.pinnedArrayRow = arrayRow;
      this.pinnedStringRow = stringRow;
    },

    resolveSelected(item) {
      void this.selectionUiVersion;
      return this.isSelectedFn ? this.isSelectedFn(item) : false;
    },

    handleToggleExpand({ item, expand }) {
      if (!item?.id) {
        return;
      }
      const next =
        typeof expand === 'boolean' ? expand : !this.expandedIds[item.id];
      if (next && item.lazyModules && !item.children?.length) {
        this.$emit('hydrate-string-modules', item.id);
      }
      this.$set(this.expandedIds, item.id, next);
      this.$nextTick(this.updatePinnedHierarchyRows);
    },

    getEffectiveTreeChildren(node) {
      if (!node) return [];
      if (node.lazyModules) {
        const lazyChildren = getPanelTreeModuleChildren(node.id);
        if (lazyChildren.length) {
          return lazyChildren;
        }
      }
      if (node.children?.length) return node.children;
      return [];
    },

    nodeHasExpandableChildren(item) {
      if (item.children?.length > 0) return true;
      if (item.moduleChildCount > 0) return true;
      if (item.lazyModules) return true;
      return false;
    },

    /** 키보드 네비게이션·포커스용 — 현재 화면에 보이는(펼침 반영) 노드 목록 */
    getVisibleNodeItems() {
      return this.flatRows.map(row => row.item);
    },

    /** Shift 범위 선택용 — parentId·type이 있는 행 */
    getVisibleNodeRows() {
      return this.flatRows;
    },

    expandAncestorsOfNodeId(nodeId) {
      const findPath = (nodes, targetId, path) => {
        if (!nodes) {
          return null;
        }
        for (const node of nodes) {
          const nextPath = [...path, node];
          if (node.id === targetId) {
            return nextPath;
          }
          const children = this.getEffectiveTreeChildren(node);
          if (children.length) {
            const found = findPath(children, targetId, nextPath);
            if (found) {
              return found;
            }
          }
        }
        return null;
      };
      const path = findPath(this.treeData, nodeId, []);
      if (!path || path.length < 2) {
        return;
      }
      path.slice(0, -1).forEach((ancestor) => {
        if (ancestor.lazyModules && !ancestor.children?.length) {
          this.$emit('hydrate-string-modules', ancestor.id);
        }
        if (this.nodeHasExpandableChildren(ancestor)) {
          this.$set(this.expandedIds, ancestor.id, true);
        }
      });
    },

    ensureNodeExpanded(nodeId) {
      if (nodeId != null) {
        this.$set(this.expandedIds, String(nodeId), true);
      }
    },

    scrollToNodeId(nodeId) {
      const scrollToIndex = () => {
        const index = this.flatRows.findIndex((row) => row.item?.id === nodeId);
        if (index < 0) {
          return false;
        }
        const scroll = this.$refs.virtualScroll;
        if (scroll && typeof scroll.scrollTo === 'function') {
          scroll.scrollTo(index);
          return true;
        }
        return false;
      };

      if (scrollToIndex()) {
        return true;
      }
      this.expandAncestorsOfNodeId(nodeId);
      this.$nextTick(() => scrollToIndex());
      return true;
    }
  }
};
</script>

<style scoped>
.module-tree-scroll-region {
  position: relative;
  min-height: 0;
  height: 100%;
}

.module-tree-sticky-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 16px;
  z-index: 3;
}

.module-tree-sticky-header {
  overflow: hidden;
  background: var(--v-surface-base);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  border: 1px solid color-mix(in srgb, var(--v-secondary-base) 55%, transparent);
  border-bottom: 1px solid var(--v-borderSubtle-base);
}

.module-tree-sticky-header:first-child {
  border-radius: 4px 4px 0 0;
}

.module-tree-sticky-header:last-child:not(:only-child) {
  border-radius: 0;
  box-shadow: none;
  border-bottom: none;
}

.module-tree-sticky-header:only-child {
  border-radius: 4px 4px 0 0;
}

.module-tree-virtual-row {
  box-sizing: border-box;
  overflow: hidden;
}

.module-tree-sticky-header >>> .tree-node-row--pinned .tree-row-main {
  background-color: var(--v-surface-base);
}

.module-tree-virtual-scroll {
  flex: 1;
  min-height: 0;
  height: 100%;
}

.module-tree-virtual-scroll >>> .v-virtual-scroll__container {
  border: 1px solid color-mix(in srgb, var(--v-secondary-base) 55%, transparent);
  border-radius: 4px;
  background: color-mix(in srgb, var(--v-surface-base) 96%, var(--v-text-base) 4%);
}

.module-tree-virtual-scroll >>> .v-virtual-scroll__item {
  padding: 0;
}
</style>
